import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { RiderLayout } from "@/components/rider/RiderLayout";
import { MobileEventSelectorV2 } from "@/components/rider/MobileEventSelectorV2";
import { DesktopEventSelectorV2 } from "@/components/rider/desktop/DesktopEventSelectorV2";
import { LoadingEvents } from "@/components/rider/LoadingEvents";
import { VehicleSelector } from "@/components/rider/VehicleSelector";
import { CommunityEvent } from "@/types/rider";
import { getEventById } from "@/services/riderService";

// Vehicle options will be dynamically set based on event data
// Default fallback if API doesn't provide vehicle options
const defaultVehicleOptions = [
  {
    id: "party-bus",
    name: "Party Bus",
    capacity: 20,
    pricing: { "4": 1000, "8": 1000 },
    icon: "🚌"
  }
];

const RiderEventSelectTest1 = () => {
  const [events, setEvents] = useState<CommunityEvent[]>([]);
  const [vehicleOptions, setVehicleOptions] = useState(defaultVehicleOptions);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();
  const [isImmersive, setIsImmersive] = useState(false);

  // Test event ID from the database
  const TEST_EVENT_ID = "898bab71-1514-404c-9d79-093629f36c81";

  useEffect(() => {
    const loadEventData = async () => {
      try {
        console.log("Loading test event with ID:", TEST_EVENT_ID);
        const eventData = await getEventById(TEST_EVENT_ID);
        
        if (eventData && eventData.pricingOptions) {
          console.log("Received event data with pricing options:", eventData);
          
          // Use the actual pricing options from the API
          const pricingOptions = eventData.pricingOptions;
          
          // Create event variants based on actual pricing options
          const eventVariants: CommunityEvent[] = pricingOptions.map((option, index) => ({
            ...eventData,
            id: `${eventData.id}-${option.duration_hours}hour-${option.is_round_trip ? 'roundtrip' : 'oneway'}`,
            startTime: `${option.duration_hours} Hour Reservation | ${option.is_round_trip ? 'Round Trip' : 'One Way'}`,
            price: option.rider_price
          }));
          
          setEvents(eventVariants);
          
          // Create vehicle options with correct pricing based on duration
          const eventVehicleOptions = [
            {
              id: "party-bus",
              name: "Party Bus",
              capacity: pricingOptions[0].passenger_count || 20,
              pricing: {
                "4": pricingOptions.find(opt => opt.duration_hours === 4)?.rider_price || 1000,
                "8": pricingOptions.find(opt => opt.duration_hours === 8)?.rider_price || 1600
              },
              icon: "🚌"
            }
          ];
          
          setVehicleOptions(eventVehicleOptions);
        } else {
          console.log("Using fallback pricing data");
          // Fallback to default pricing structure
          const fallbackEvent = eventData || {
            id: TEST_EVENT_ID,
            name: "Test Event",
            location: "Test Venue",
            region: "Test Region",
            image: "/Default_Party_Bus.jpeg",
            date: "2025-10-03",
            startTime: "6:00 PM",
            price: 1000
          };
          
          // Create event variants based on default pricing structure
          const eventVariants: CommunityEvent[] = [
            {
              ...fallbackEvent,
              id: `${fallbackEvent.id}-4hour-oneway`,
              startTime: "4 Hour Reservation | One Way",
              price: 1000
            },
            {
              ...fallbackEvent,
              id: `${fallbackEvent.id}-8hour-roundtrip`,
              startTime: "8 Hour Reservation | Round Trip", 
              price: 1600
            }
          ];
          
          setEvents(eventVariants);
          
          // Set vehicle options with default pricing: $1000 for 4-hour, $1600 for 8-hour
          const eventVehicleOptions = [
            {
              id: "party-bus",
              name: "Party Bus",
              capacity: 20,
              pricing: { "4": 1000, "8": 1600 },
              icon: "🚌"
            }
          ];
          
          setVehicleOptions(eventVehicleOptions);
        }
      } catch (error) {
        console.error("Failed to load test event:", error);
        // Use fallback data with default pricing
        const fallbackEvents: CommunityEvent[] = [
          {
            id: `${TEST_EVENT_ID}-4hour-oneway`,
            name: "Test Event",
            location: "Test Venue",
            region: "Test Region",
            image: "/Default_Party_Bus.jpeg",
            date: "2025-10-03",
            startTime: "4 Hour Reservation | One Way",
            price: 1000
          },
          {
            id: `${TEST_EVENT_ID}-8hour-roundtrip`,
            name: "Test Event",
            location: "Test Venue", 
            region: "Test Region",
            image: "/Default_Party_Bus.jpeg",
            date: "2025-10-03",
            startTime: "8 Hour Reservation | Round Trip",
            price: 1600
          }
        ];
        
        setEvents(fallbackEvents);
        
        // Fallback vehicle options with default pricing
        const fallbackVehicleOptions = [
          {
            id: "party-bus",
            name: "Party Bus",
            capacity: 20,
            pricing: { "4": 1000, "8": 1600 },
            icon: "🚌"
          }
        ];
        
        setVehicleOptions(fallbackVehicleOptions);
      } finally {
        setLoading(false);
      }
    };

    loadEventData();
  }, []);

  const handleEventSelection = (eventId: string, index: number) => {
    setSelectedEventId(eventId || null);
    setActiveIndex(index);
  };

  const handleResetSelection = () => {
    setSelectedEventId(null);
    setSelectedVehicleId(null);
    setActiveIndex(0);
    setIsImmersive(false);
  };

  const handleVehicleSelection = (vehicleId: string) => {
    setSelectedVehicleId(vehicleId);
  };

  const handleBackToEvents = () => {
    setSelectedEventId(null);
    setSelectedVehicleId(null);
    setIsImmersive(false);
  };

  const handleBackToVehicles = () => {
    setSelectedVehicleId(null);
  };

  const handleContinue = (selectedAddOns?: any[], totalPrice?: number) => {
    if (selectedEventId && selectedVehicleId) {
      const selectedEvent = events.find(event => event.id === selectedEventId);
      const selectedVehicle = vehicleOptions.find(vehicle => vehicle.id === selectedVehicleId);
      const isRoundTrip = selectedEvent?.startTime?.includes('8 Hour') ? 'true' : 'false';
      const venueAddress = selectedEvent?.location || "Test Venue";
      
      const params = new URLSearchParams({ 
        eventId: TEST_EVENT_ID, // Use original event ID for API calls
        capacity: selectedVehicle?.capacity.toString() || '20',
        isRoundTrip: isRoundTrip,
        destination: venueAddress,
        vehicleType: selectedVehicle?.name || ''
      });
      
      // For round trips, handle return trip information
      if (isRoundTrip === 'true') {
        // Check if after-party add-on is selected for return destination
        const afterPartyAddon = selectedAddOns?.find(addon => 
          addon.description.includes('after-party') || addon.description.includes('after party')
        );
        
        if (afterPartyAddon) {
          // Set appropriate after-party destinations for test events
          params.append('returnDestination', 'Downtown');
          params.append('returnPickup', venueAddress);
        } else {
          params.append('returnDestination', '');
          params.append('returnPickup', venueAddress);
        }
      }
      
      if (totalPrice) {
        params.append('price', totalPrice.toString());
      }
      
      // Create notes with vehicle information
      let notes = `${selectedVehicle?.name} - Capacity ${selectedVehicle?.capacity}`;
      
      if (selectedAddOns && selectedAddOns.length > 0) {
        params.append('addOns', JSON.stringify(selectedAddOns));
        
        const addOnNotes = selectedAddOns
          .map(addon => `${addon.description} (+$${addon.price})`)
          .join('\n');
        notes += '\n' + addOnNotes;
      }
      
      params.append('notes', notes);
      
      navigate(`/rider-quote?${params.toString()}`);
    }
  };

  const handleManualEntry = () => {
    handleResetSelection();
  };

  const handlePrevCard = () => {
    if (activeIndex > 0) {
      setActiveIndex(prev => prev - 1);
      setSelectedEventId(events[activeIndex - 1].id);
    }
  };

  const handleNextCard = () => {
    if (activeIndex < events.length - 1) {
      setActiveIndex(prev => prev + 1);
      setSelectedEventId(events[activeIndex + 1].id);
    }
  };

  // Mobile-specific navigation handlers that only change activeIndex
  const handleMobilePrevCard = () => {
    if (activeIndex > 0) {
      setActiveIndex(prev => prev - 1);
    }
  };

  const handleMobileNextCard = () => {
    if (activeIndex < events.length - 1) {
      setActiveIndex(prev => prev + 1);
    }
  };

  const handleCardTap = (eventId: string, index: number) => {
    if (!isImmersive) {
      setIsImmersive(true);
    }
    handleEventSelection(eventId, index);
  };

  return (
    <RiderLayout>
      <div className="min-h-screen bg-white transition-colors duration-300">
        {loading ? (
          <LoadingEvents />
        ) : (
          <>
            <MobileEventSelectorV2
              events={events}
              selectedEventId={selectedEventId}
              selectedVehicleId={selectedVehicleId}
              vehicleOptions={vehicleOptions}
              activeIndex={activeIndex}
              onEventSelect={handleEventSelection}
              onVehicleSelect={handleVehicleSelection}
              onContinue={handleContinue}
              onManualEntry={handleManualEntry}
              onPrevCard={handleMobilePrevCard}
              onNextCard={handleMobileNextCard}
              onBackToVehicles={handleBackToVehicles}
            />

            <DesktopEventSelectorV2
              events={events}
              activeIndex={activeIndex}
              selectedEventId={selectedEventId}
              selectedVehicleId={selectedVehicleId}
              vehicleOptions={vehicleOptions}
              isImmersive={isImmersive}
              setIsImmersive={setIsImmersive}
              handleEventSelection={handleEventSelection}
              handlePrevCard={handlePrevCard}
              handleNextCard={handleNextCard}
              handleCardTap={handleCardTap}
              handleContinue={handleContinue}
              handleManualEntry={handleManualEntry}
              handleResetSelection={handleResetSelection}
              handleVehicleSelection={handleVehicleSelection}
              handleBackToVehicles={handleBackToVehicles}
            />
          </>
        )}
      </div>
    </RiderLayout>
  );
};

export default RiderEventSelectTest1;
